import { defineStore } from 'pinia'
import dayjs from 'dayjs'
import {
  LicenceOut,
  SoftwareOut,
  SoftwareLicense,
  QuotaInformation_Output,
  QuotaStatus,
  PriceInformation,
  BillingUnit,
} from '@/client'
import { Document } from '@/types/document'
import { SubAccount } from '@/types/software'
import { Statistic } from '@/types/statistics'
import { useContractStore } from '@/stores/contractStore'
import { AccountStore } from '@/stores/account/account.store'
import { OptimizationTypes } from '@/stores/optimization/optimization.models'
import { getSoftwareLicenses } from '@/utils/license'
import { ApplicationStore } from '@/stores/application/application.store'

export const useApplicationDetailsStore = defineStore(
  'applicationDetailsViewStore',
  () => {
    const application = ref<SoftwareOut>()
    const optimizations = ref<OptimizationTypes[]>()
    const documents = ref<Document[]>()
    const costStatistics = ref<Statistic[]>()
    const currentCost = ref<number>()
    const selectedSubAccount = ref<SubAccount>()
    const quota = ref<Record<string, QuotaInformation_Output>>({})
    const licences = ref<SoftwareLicense[]>([])
    const licencesForApplicationId = ref<string | null>(null)
    const currentApplicationId = ref<string | null>(null)
    const loadingApplication = ref(false)
    const loadingLicences = ref(false)
    const licencesTable = ref<{
      selectedLicences?: LicenceOut[]
      searchQuery?: string
      isLoading: boolean
      onlyShowInvites: boolean
    }>({
      selectedLicences: undefined,
      searchQuery: '',
      isLoading: false,
      onlyShowInvites: false,
    })

    function initApplication(loadedApplication: SoftwareOut | undefined) {
      application.value = loadedApplication
      currentApplicationId.value = loadedApplication?._id ?? null
    }

    function initLicenseModels(loadedLicenses: SoftwareLicense[]) {
      licences.value = loadedLicenses
    }

    async function loadSoftwareLicenses(applicationId: string, force = false) {
      if (
        !force &&
        licences.value.length > 0 &&
        licencesForApplicationId.value === applicationId
      ) {
        return
      }

      loadingLicences.value = true
      try {
        const response = await getSoftwareLicenses(applicationId)
        const mapped = response.map((item) => item.license)
        initLicenseModels(mapped)
        licencesForApplicationId.value = applicationId
      } finally {
        loadingLicences.value = false
      }
    }

    async function loadApplicationDetails(
      applicationId: string,
      force = false
    ) {
      if (
        !force &&
        currentApplicationId.value === applicationId &&
        application.value
      ) {
        return
      }

      loadingApplication.value = true
      try {
        const loadedApplication = await ApplicationStore.getApplicationById(
          applicationId
        )

        if (!loadedApplication) {
          application.value = undefined
          currentApplicationId.value = null
          licences.value = []
          return
        }

        initApplication(loadedApplication)
        await loadSoftwareLicenses(loadedApplication._id, force)
      } finally {
        loadingApplication.value = false
      }
    }

    async function refreshLicences() {
      if (!currentApplicationId.value) return
      await loadSoftwareLicenses(currentApplicationId.value, true)
    }

    const _getActiveContract = (contracts: PriceInformation[]) => {
      if (!contracts) return []

      const activeContracts = ref<PriceInformation[]>([])

      for (const contract of contracts) {
        const fromDate = contract.from_date ? dayjs(contract.from_date) : null
        const endDate = contract.end_date ? dayjs(contract.end_date) : null

        if (
          (fromDate && endDate && dayjs().isBetween(fromDate, endDate)) ||
          !fromDate ||
          !endDate
        ) {
          activeContracts.value.push(contract)
        }
      }

      return activeContracts.value
    }

    async function loadQuota(licenseId: string) {
      const contractStore = useContractStore()

      // load relevant data
      const cachedContract = contractStore.getContract(licenseId) ?? []
      const activeContracts = _getActiveContract(cachedContract)

      const license = licences.value.find(
        (license) => license._id === licenseId
      )

      if (!cachedContract.length && !license?.quota_information) return
      const currentCount = await AccountStore.getAccountsCount({
        softwareLicenses: [licenseId],
      })

      // check cases
      // for more infos to explain the workflow look into the affine board
      const externalQuota = license?.quota_information ?? undefined
      const hasManualContract = activeContracts.some(
        (contract: PriceInformation) =>
          contract.billing_unit === BillingUnit.QUOTA &&
          contract.quota != null &&
          contract.quota > 0
      )
      const totalManualQuota = activeContracts.reduce(
        (sum, obj) => sum + (obj.quota || 0),
        0
      )
      const manualQuotaIsLargerThenExternal = externalQuota
        ? Boolean(totalManualQuota > externalQuota?.quota)
        : true

      const quotaInformation: Ref<QuotaInformation_Output | undefined> = ref()

      if (hasManualContract || externalQuota) {
        // there is some kind of quota information available

        if (manualQuotaIsLargerThenExternal) {
          // overwrite external quota
          quotaInformation.value = {
            quota: totalManualQuota,
            used: currentCount,
            available: totalManualQuota - currentCount,
            status: QuotaStatus.AVAILABLE, // deprecated?
            free_quota: 0, //deprecated?
          }
        } else {
          // use external quota
          if (!externalQuota) {
            throw new Error('External Quota should be defined')
          }
          quotaInformation.value = {
            quota: externalQuota.quota,
            used: currentCount,
            available: externalQuota.quota - currentCount,
            status: QuotaStatus.AVAILABLE, // deprecated?
            free_quota: 0, //deprecated?
          }
        }
      }

      // handle quota information cache
      if (quotaInformation.value) {
        quota.value[licenseId] = quotaInformation.value
      } else {
        delete quota.value[licenseId]
      }
    }

    async function loadAllQuotas(values: SoftwareLicense[]) {
      if (!application.value) return
      const contractStore = useContractStore()
      await contractStore.loadContracts(application.value._id)

      for (const key in values) {
        const license: SoftwareLicense = values[key]
        loadQuota(license._id)
      }
    }

    function updateQuota(afterIds: string[] = [], beforeIds: string[] = []) {
      const removedIds = beforeIds.filter((id) => !afterIds.includes(id))
      const addedIds = afterIds.filter((id) => !beforeIds.includes(id))

      for (const id of removedIds) {
        const cachedQuota = quota.value[id]
        if (cachedQuota) {
          cachedQuota.used -= 1
          cachedQuota.available = cachedQuota.quota - cachedQuota.used
        }
      }

      for (const id of addedIds) {
        const cachedQuota = quota.value[id]
        if (cachedQuota) {
          cachedQuota.used += 1
          cachedQuota.available = cachedQuota.quota - cachedQuota.used
        }
      }
    }

    function $reset() {
      application.value = undefined
      optimizations.value = undefined
      documents.value = undefined
      costStatistics.value = undefined
      currentCost.value = undefined
      selectedSubAccount.value = undefined
      quota.value = {}
      licences.value = []
      licencesForApplicationId.value = null
      currentApplicationId.value = null
      loadingApplication.value = false
      loadingLicences.value = false
      licencesTable.value = {
        selectedLicences: undefined,
        searchQuery: '',
        isLoading: false,
        onlyShowInvites: false,
      }
    }

    return {
      loadApplicationDetails,
      loadSoftwareLicenses,
      refreshLicences,
      initApplication,
      initLicenseModels,
      $reset,
      application,
      optimizations,
      documents,
      costStatistics,
      currentCost,
      selectedSubAccount,
      licences,
      currentApplicationId,
      loadingApplication,
      loadingLicences,
      licencesTable,

      // Quota related
      quota,
      loadQuota,
      loadAllQuotas,
      updateQuota,
    }
  }
)
